import { useZero } from "@rocicorp/zero/react";
import { keyframes, styled } from "goober";
import { nanoid } from "nanoid";
import { useEffect, useEffectEvent, useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import { isWebpageRoute } from "../../shared/stepConfig";
import { usePanelShortcut } from "../hooks/usePanelShortcut";
import { useNavigateOnBranchChange } from "../hooks/useNavigateOnBranchChange";
import { usePinnedPanel } from "../hooks/usePinnedPanel";
import { useSelectedExecution } from "../hooks/useSelectedExecution";
import { useStepExecutions } from "../hooks/useStepExecutions";
import { useTenantCustomDomain } from "../hooks/useTenantCustomDomain";
import { useSessionTenantEdgeUrlOptions } from "../hooks/useSessionTenantOrigin";
import { type ConnectorInfo, splitStepConnectors } from "../lib/connector";
import type {
  StreamingBashWrite,
  StreamingFileEdit,
} from "../lib/parseStreamingBashWrite";
import {
  getStepCopyableEmailAddress,
  getStepCopyableUrl,
} from "../lib/stepUrl";
import { useMakeUrl, navigate, routes, useCurrentUrl } from "../lib/urls";
import { stepStorageVolumeName } from "../lib/workflowStoragePath";
import { Button } from "./Button";
import type { CodeSelection } from "./CodeEditor";
import { ConnectorsBar } from "./ConnectorsBar";
import { StepCopyButton } from "./StepCopyButton";
import { PanelHeaderActions } from "./PanelHeader";
import { PlayButtons } from "./PlayButtons";
import { ChatpadToggleButton } from "./PersonalChats/ChatpadToggleButton";
import {
  resolveStepTab,
  stepTabs,
  stepTabShortcuts,
  type StepTab,
} from "./stepTab";
import {
  pinnedByExecutionId,
  type StepTestFixtureSetupRequest,
} from "../lib/pinnedExecutions";
import { LiveBrowser } from "./LiveBrowser";
import { useSettledStepContentKey } from "../lib/stepContentKey";
import { StepExecutions } from "./StepExecutions";
import {
  StepExecutionPicker,
  StepExecutionSidebar,
} from "./StepExecutionSidebar";
import { ExecutionPanel, ExecutionPanelContent } from "./ExecutionPanel";
import { executionsShortcut, PinnedPanelSlot } from "./PinnedPanel";
import { type StepFileData, StepFiles } from "./StepFiles";
import { StepHeader } from "./StepHeader";
import { StepStoragePanel } from "./StepStoragePanel";
import { Tooltip } from "./Tooltip";
import type { CodeSnippet, ElementSnippet } from "./Chat/chatTypes";
import type { BranchData, CommitFileData, StepData } from "./Workflow";

const Container = styled("div")`
  display: flex;
  flex-direction: column;
  height: 0;
  flex: 1;
  min-width: 0;
`;

const Body = styled("div")`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
  height: 0;
  padding: 0 12px;
`;

const HeaderActions = styled(PanelHeaderActions)`
  min-width: 0;
  flex-shrink: 1;
  gap: 6px;
  /* Room for focus rings, which the horizontal scroll would otherwise clip. */
  padding: 4px;
  margin: -4px -4px -4px auto;
  overflow-x: auto;
  scrollbar-width: none;
`;

const rotateFollowRing = keyframes`
  to { --follow-ring-angle: 360deg; }
`;

const PaneRing = styled(ExecutionPanel)`
  &[data-follow-ring="true"]::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 12px;
    padding: 1.5px;
    box-sizing: border-box;
    pointer-events: none;
    z-index: 2;
    background: conic-gradient(
      from var(--follow-ring-angle),
      var(--pink-600) 0deg 180deg,
      var(--sky-600) 180deg 270deg,
      var(--orange-600) 270deg 313.2deg,
      var(--teal-600) 313.2deg 360deg
    );
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    mask-composite: exclude;
    animation: ${rotateFollowRing} 5000ms linear infinite;
  }
`;

export function MaximizedStep({
  onClosePanel,
  step,
  allSteps,
  spaceSlug,
  customHostname,
  spaceId,
  allConnectors,
  connectorsReady,
  branch,
  commitId,
  headCommitFiles,
  files,
  baseFiles,
  isReadOnly = false,
  canWrite: hasWriteAccess,
  workflowPausedAt,
  onInvestigateFailure,
  onAskSetupStepTest,
  onSelectionChange,
  codeSnippets,
  isBeingWritten,
  forceShowCode = false,
  onStopFollow,
  expandOutput,
  followActive,
  streamingFileWrite,
  streamingFileEdit,
  onElementSelect,
  onElementDeselect,
  htmlInspectMode,
  onHtmlInspectModeChange,
  elementSnippets,
}: {
  step: StepData;
  allSteps: StepData[];
  spaceSlug: string;
  customHostname?: string | null;
  spaceId: string;
  allConnectors: readonly ConnectorInfo[];
  branch: BranchData;
  commitId: string;
  headCommitFiles: readonly CommitFileData[];
  files: readonly StepFileData[];
  baseFiles?: readonly StepFileData[];
  isReadOnly?: boolean;
  canWrite: boolean;
  connectorsReady: boolean;
  workflowPausedAt: number | null;
  onInvestigateFailure: (params: { stepDir: string; testName: string }) => void;
  onAskSetupStepTest: (request: StepTestFixtureSetupRequest) => void;
  onSelectionChange?: (selections: CodeSelection[]) => void;
  codeSnippets?: readonly CodeSnippet[];
  onElementSelect?: (
    element: Omit<ElementSnippet, "stepName" | "color">
  ) => void;
  onElementDeselect?: (index: number) => void;
  htmlInspectMode?: boolean;
  onHtmlInspectModeChange?: (mode: boolean) => void;
  elementSnippets?: readonly ElementSnippet[];
  isBeingWritten?: boolean;
  forceShowCode?: boolean;
  onStopFollow?: () => void;
  onClosePanel: () => void;
  expandOutput?: boolean;
  followActive?: boolean;
  streamingFileWrite?: StreamingBashWrite | null;
  streamingFileEdit?: StreamingFileEdit | null;
}) {
  const canWrite = hasWriteAccess && !isReadOnly;
  const makeUrl = useMakeUrl();
  const z = useZero();
  const branchId = branch.id;
  const isOnMainBranch = branch.name === "main";
  const contentKey = useSettledStepContentKey(
    step.canonicalId,
    headCommitFiles,
    branch.headCommitChat?.respondingAt != null
  );
  const navigateOnBranchChange = useNavigateOnBranchChange(branchId);
  const {
    workflowId,
    stepTab: urlStepTab,
    showRuns,
    file,
    execution: urlExecutionId,
  } = useCurrentUrl(routes.space.workflow);
  const customDomain = useTenantCustomDomain();

  const {
    connected: associatedConnectors,
    associatedIds: associatedConnectorIds,
    disconnected: disconnectedStepApps,
  } = splitStepConnectors(step.connectedApps ?? [], allConnectors);

  const associatedConnectorIdSet = new Set(associatedConnectorIds);
  // allConnectors is already filtered to what this space can use, so the picker
  // offers only those.
  const spaceUsableConnectorIds = new Set(allConnectors.map((app) => app.id));
  const [reEmitTurnKey, setReEmitTurnKey] = useState(0);
  const { executions, loading: executionsLoading } = useStepExecutions(
    workflowId,
    branchId,
    step.id
  );
  const { selected: selectedExecution } = useSelectedExecution(
    step.id,
    workflowId,
    branchId
  );
  const storageVolumeName = stepStorageVolumeName(
    files.find((f) => f.path === "Dockerfile")?.content
  );
  const pinnedByExecId = pinnedByExecutionId(files);

  const handleReEmit = (executionId: string) => {
    if (!canWrite) return;
    setReEmitTurnKey((key) => key + 1);
    z.mutate(
      sharedMutators.reEmitRetainedExecution({
        executionId,
        newExecutionId: nanoid(12),
        workflowId,
      })
    );
  };

  const browserApplicable = isWebpageRoute(step.routeType);
  const tab = resolveStepTab({
    stepTab: urlStepTab,
    showRuns,
    file,
    browserApplicable,
    execution: urlExecutionId,
    forceShowCode,
    expandOutput,
  });
  const {
    layoutRef: executionsLayoutRef,
    measured: executionsMeasured,
    open: executionsOpen,
    pinned: executionsPinned,
    togglePinned: toggleExecutionsPinned,
  } = usePinnedPanel("executionsPinned");
  usePanelShortcut(
    executionsShortcut,
    toggleExecutionsPinned,
    tab === "output"
  );

  const associateApp = (connectedAppId: string, slug?: string) =>
    navigateOnBranchChange((branchParams) =>
      z.mutate(
        sharedMutators.associateStepWithConnectedApp({
          ...branchParams,
          stepDir: step.name,
          connectedAppId,
          ...(slug && { slug }),
        })
      )
    );

  const disassociateApp = (connectedAppId: string) => {
    void navigateOnBranchChange((branchParams) =>
      z.mutate(
        sharedMutators.dissociateStepFromConnectedApp({
          ...branchParams,
          stepDir: step.name,
          connectedAppId,
        })
      )
    );
  };

  const selectTab = useEffectEvent((next: StepTab) => {
    onStopFollow?.();
    navigate(null, { stepTab: next, showRuns: "" });
  });
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (
        event.defaultPrevented ||
        event.repeat ||
        event.isComposing ||
        !(event.metaKey || event.ctrlKey) ||
        !event.shiftKey ||
        event.altKey ||
        document.querySelector('dialog[open], [role="dialog"]')
      )
        return;
      const next = stepTabShortcuts.find(
        (tab) => `Digit${tab.shortcut}` === event.code
      )?.key;
      if (!next) return;
      event.preventDefault();
      selectTab(next);
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const edgeUrlOptions = useSessionTenantEdgeUrlOptions();
  const stepUrl = getStepCopyableUrl(
    step,
    branchId,
    isOnMainBranch,
    spaceSlug,
    edgeUrlOptions,
    customDomain,
    customHostname
  );
  const stepEmail = getStepCopyableEmailAddress(
    step,
    branchId,
    isOnMainBranch,
    spaceSlug,
    edgeUrlOptions,
    customDomain,
    customHostname
  );

  return (
    <Container>
      <StepHeader
        step={step}
        tab={tab}
        browserApplicable={browserApplicable}
        onNavigate={onStopFollow}
        backTo={makeUrl(routes.space.workflow, {
          spaceId,
          workflowId,
          step: "",
          file: "",
          stepTab: "",
          showRuns: "",
          execution: "",
          view: "",
          stepTest: "",
        })}
        right={
          <>
            <HeaderActions>
              <ConnectorsBar
                parentNoun="step"
                avatarSize={32}
                canWrite={canWrite}
                disabled={!connectorsReady}
                spaceId={spaceId}
                connected={associatedConnectors}
                excludeIds={associatedConnectorIdSet}
                availableIds={spaceUsableConnectorIds}
                disconnected={disconnectedStepApps.map((sca) => ({
                  id: sca.slug,
                  appType: sca.appType,
                  slug: sca.slug,
                }))}
                associate={associateApp}
                remove={disassociateApp}
                afterCreate={(newAppId) => {
                  navigate(routes.space.workflow, {
                    spaceId,
                    workflowId,
                    step: step.canonicalId,
                  });
                  navigate(routes.modal.connector, { id: newAppId });
                }}
              />
              {stepUrl && <StepCopyButton text={stepUrl} icon="link-2" />}
              {stepEmail && <StepCopyButton text={stepEmail} icon="at-sign" />}
              <PlayButtons
                onExecution={(id) => {
                  onStopFollow?.();
                  navigate(null, {
                    execution: id,
                    stepTab: "output",
                    showRuns: "",
                  });
                }}
                step={step}
                commitId={commitId}
                branchId={branchId}
                disabled={!canWrite}
                disabledReason={
                  isOnMainBranch && workflowPausedAt != null
                    ? "Workflow is paused"
                    : undefined
                }
              />
            </HeaderActions>
            <ChatpadToggleButton isOpen onToggle={onClosePanel} />
          </>
        }
      />
      <Body
        role="region"
        aria-label={`${stepTabs.find((item) => item.key === tab)?.label} for ${step.name}`}
      >
        <PaneRing
          ref={executionsLayoutRef}
          data-follow-ring={followActive ? "true" : undefined}
          data-framed={tab === "output" || tab === "code" || undefined}
        >
          <ExecutionPanelContent>
            {tab === "code" ? (
              <StepFiles
                embedded
                files={files}
                previousFiles={baseFiles}
                canShowDiff={baseFiles != null}
                stepName={step.name}
                allStepNames={allSteps.map((s) => s.name)}
                onSelectionChange={onSelectionChange}
                codeSnippets={codeSnippets}
                streamingFileWrite={streamingFileWrite ?? null}
                streamingFileEdit={streamingFileEdit ?? null}
              />
            ) : tab === "files" ? (
              <StepStoragePanel
                spaceId={spaceId}
                branch={branch}
                volumeName={storageVolumeName}
                canWrite={canWrite}
              />
            ) : tab === "browser" ? (
              <LiveBrowser
                step={step}
                spaceSlug={spaceSlug}
                spaceId={spaceId}
                branchId={branchId}
                contentKey={contentKey}
                navBranchId={isOnMainBranch ? undefined : branchId}
                displayUrl={(stepUrl ?? "").replace(/^https?:\/\//, "")}
                openUrl={stepUrl ?? undefined}
                inspectMode={htmlInspectMode}
                onInspectModeChange={onHtmlInspectModeChange}
                onElementSelect={onElementSelect}
                onElementDeselect={onElementDeselect}
                elementSnippets={elementSnippets}
              />
            ) : (
              <StepExecutions
                canWrite={canWrite}
                embedded
                headerActions={
                  executionsMeasured &&
                  !executionsOpen && (
                    <StepExecutionPicker
                      executions={executions}
                      selectedExecution={selectedExecution}
                      pinnedByExecId={pinnedByExecId}
                      pinned={executionsPinned}
                      onTogglePinned={toggleExecutionsPinned}
                      onNavigate={onStopFollow}
                    />
                  )
                }
                step={step}
                branchId={branchId}
                commitId={commitId}
                files={files}
                isMainBranch={isOnMainBranch}
                pinnedByExecId={pinnedByExecId}
                onSelectionChange={onSelectionChange}
                codeSnippets={codeSnippets}
                isBeingWritten={isBeingWritten}
                onInvestigateFailure={onInvestigateFailure}
                onAskSetupStepTest={onAskSetupStepTest}
              />
            )}
          </ExecutionPanelContent>
          {tab === "output" && (
            <PinnedPanelSlot
              measured={executionsMeasured}
              open={executionsOpen}
            >
              <StepExecutionSidebar
                executions={executions}
                selectedExecution={selectedExecution}
                loading={executionsLoading}
                pinnedByExecId={pinnedByExecId}
                workflowId={workflowId}
                branchId={branchId}
                stepDir={step.id}
                onNavigate={onStopFollow}
                onCollapse={toggleExecutionsPinned}
                action={
                  selectedExecution?.status === "success" &&
                  (step.links ?? []).length > 0 ? (
                    <Tooltip content="Re-emit">
                      <Button
                        icon="refresh-cw"
                        size="m"
                        aria-label="Re-emit"
                        disabled={!canWrite}
                        iconTurnKey={reEmitTurnKey}
                        variant="secondary"
                        onClick={() => handleReEmit(selectedExecution.id)}
                      />
                    </Tooltip>
                  ) : undefined
                }
              />
            </PinnedPanelSlot>
          )}
        </PaneRing>
      </Body>
    </Container>
  );
}
